import axios from 'axios';
import toast from 'react-hot-toast';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth API instance (for authentication endpoints)
export const authAPI = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if session was terminated by login block
      if (error.response?.data?.sessionTerminated) {
        sessionStorage.removeItem('token');
        window.location.href = '/login';
        toast.error('Your session has been terminated. Please login again.');
      } else {
        // Token expired or invalid
        sessionStorage.removeItem('token');
        window.location.href = '/login';
        toast.error('Session expired. Please login again.');
      }
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Insufficient permissions.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please try again.');
    } else if (!error.response) {
      toast.error('Network error. Please check your connection.');
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
export const productsAPI = {
  // Get all products
  getAll: (params = {}) => api.get('/products', { params }),
  
  // Get single product
  getById: (id) => api.get(`/products/${id}`),
  
  // Create product (admin)
  create: (data) => api.post('/products', data),
  
  // Update product (admin)
  update: (id, data) => api.put(`/products/${id}`, data),
  
  // Delete product (admin)
  delete: (id) => api.delete(`/products/${id}`),
  
  // Get product variants
  getVariants: (productId) => api.get(`/products/${productId}/variants`),
  
  // Create variant (admin)
  createVariant: (productId, data) => api.post(`/products/${productId}/variants`, data),
  
  // Update variant (admin)
  updateVariant: (productId, variantId, data) => 
    api.put(`/products/${productId}/variants/${variantId}`, data),
  
  // Delete variant (admin)
  deleteVariant: (productId, variantId) => 
    api.delete(`/products/${productId}/variants/${variantId}`),
};

export const ordersAPI = {
  // Create order
  create: (data) => api.post('/orders', data),
  
  // Get order history
  getAll: (params = {}) => api.get('/orders', { params }),
  
  // Get single order
  getById: (id) => api.get(`/orders/${id}`),
  
  // Get order by order number
  getByNumber: (orderNumber) => api.get(`/orders/number/${orderNumber}`),
  
  // Retry failed order (admin)
  retry: (id) => api.post(`/orders/${id}/retry`),
  
  // Delete order (admin)
  delete: (id) => api.delete(`/orders/${id}`),
  
  // Get notes for an order
  getNotes: (id) => api.get(`/orders/${id}/notes`),
  
  // Save note for an order
  saveNote: (id, note) => api.post(`/orders/${id}/notes`, { note }),
};

export const keysAPI = {
  // Get all keys/deliverables
  getAll: (params = {}) => api.get('/keys', { params }),
  
  // Get single key/deliverable
  getById: (id) => api.get(`/keys/${id}`),
};

export const dashboardAPI = {
  // Get dashboard statistics
  getStats: (params = {}) => api.get('/dashboard/stats', { params }),
  
  // Get balance information
  getBalance: () => api.get('/dashboard/balance'),
  
  // Get recent activity
  getActivity: (params = {}) => api.get('/dashboard/activity', { params }),
  
  // Get system health
  getHealth: () => api.get('/dashboard/health'),
  
  // Get pie chart data
  getRevenueByProduct: (params = {}) => api.get('/dashboard/revenue-by-product', { params }),
  getOrdersByProduct: (params = {}) => api.get('/dashboard/orders-by-product', { params }),
  getRevenueByCategory: (params = {}) => api.get('/dashboard/revenue-by-category', { params }),
  getOrdersByCategory: (params = {}) => api.get('/dashboard/orders-by-category', { params }),
};

export const adminAPI = {
  // API Configurations
  getApiConfigs: () => api.get('/admin/api-configs'),
  getApiConfig: (id) => api.get(`/admin/api-configs/${id}`),
  createApiConfig: (data) => api.post('/admin/api-configs', data),
  updateApiConfig: (id, data) => api.put(`/admin/api-configs/${id}`, data),
  deleteApiConfig: (id) => api.delete(`/admin/api-configs/${id}`),
  testApiConfig: (id) => api.post(`/admin/api-configs/${id}/test`),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSetting: (key, data) => api.put(`/admin/settings/${key}`, data),
  
  // Statistics
  getStatistics: () => api.get('/admin/statistics'),
  
  // Data Export
  exportData: (table) => api.get(`/admin/export/${table}`),
  
  // Maintenance
  cleanupOrders: (params = {}) => api.delete('/admin/orders/cleanup', { params }),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

// Utility functions
export const handleApiError = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else {
    return 'An unexpected error occurred';
  }
};

export const formatApiResponse = (response) => {
  return {
    success: response.data.success,
    data: response.data.data,
    message: response.data.message,
  };
};

// Export default api instance
export default api;
